import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ApiError, errorMessage, get, patch } from "@/lib/api";
import type { LibraryItem } from "@/lib/types";
import type { LibraryListResponse } from "./types";

export const libraryKey = (workspaceId: string) => ["library", workspaceId] as const;
export const paperKey = (id: string) => ["library-item", id] as const;

/** A PDF uploaded as a new paper whose details are still being looked up (from the DOI printed in it). */
export function findingDetails(i: Pick<LibraryListResponse["items"][number], "title" | "attachments" | "extraFields">) {
  if (i.title.trim()) return false;
  const pdf = i.attachments.find((a) => a.mime === "application/pdf");
  if (!pdf || pdf.encrypted) return false;
  if (pdf.extractionStatus === "queued" || pdf.extractionStatus === "processing") return true;
  return (pdf.extractionStatus === "done" || pdf.extractionStatus === "partial") && !(i.extraFields as { identification?: unknown } | null)?.identification;
}

export function useLibraryList(workspaceId: string) {
  return useQuery({
    queryKey: libraryKey(workspaceId),
    queryFn: () => get<LibraryListResponse>(`/api/workspaces/${workspaceId}/library`),
    enabled: !!workspaceId,
    // While open-access downloads or text extraction run in the background, keep the list current.
    refetchInterval: (q) => {
      const items = (q.state.data as LibraryListResponse | undefined)?.items ?? [];
      const busy = items.some((i) => i.oaStatus === "pending" || i.attachments.some((a) => a.extractionStatus === "queued" || a.extractionStatus === "processing") || findingDetails(i));
      return busy ? 2500 : false;
    },
  });
}

/** PATCH a library record with optimistic-concurrency handling (409 → reload). */
export function usePatchItem(workspaceId: string) {
  const qc = useQueryClient();
  return async (item: Pick<LibraryItem, "id" | "version">, body: Record<string, unknown>) => {
    try {
      const res = await patch<{ item: LibraryItem }>(`/api/library/${item.id}`, { ...body, version: item.version });
      await Promise.all([qc.invalidateQueries({ queryKey: libraryKey(workspaceId) }), qc.invalidateQueries({ queryKey: paperKey(item.id) })]);
      return res.item;
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && !e.details?.suggestion) {
        toast.error("Someone else changed this record. The latest version has been loaded — please check and try again.");
        await Promise.all([qc.invalidateQueries({ queryKey: libraryKey(workspaceId) }), qc.invalidateQueries({ queryKey: paperKey(item.id) })]);
        return null;
      }
      if (!(e instanceof ApiError && e.status === 409)) toast.error(errorMessage(e));
      throw e;
    }
  };
}
